import { i18n, BoolHelper, MetadataConstants, MetadataSelectors, RamdaHelper } from 'invision-core';
import { createSelector } from 'reselect';
import CustomerCareLocaleKeys from '../../locales/keys';
import { countBy, filter, pathOr, prop, propEq } from 'ramda';
import { MakePaymentInstrumentSelector } from '../../components/customer/makePayment/make.payment.selectors';
import { cardTypeRequiresBillingAddress } from './customer.ewallet.selectors.helpers';
import { parseAdditionalProperties, PaymentInstrumentConfigurationAdditionalProperties, paymentInstrumentConfigurationAdditionalPropertyDescriptors } from '../../reducers/helpers/payment.instrument.additional.property.parser';
import { ActionablesPerPaymentTypeSelector, AvailableTypesMapSelector, IsAllPaymentInstrumentTypesAvailableSelector, PaymentInstrumentsSelector } from './customer.ewallet.selectors';
import { ENABLE_ACCOUNT_NUMBER_KEY, IS_TWO_STEP_PAYMENT_KEY, SUPPORTED_PAYMENT_INSTRUMENT_TYPES } from '../../components/customer/ewallet/ewallet.constants';
import * as EWalletHelper from '../helpers/ewallet.reducer.helper';
import { isPaymentInstrumentTypeValueOptionSelected, subscriberHasExternalGiftCardOfType } from './modify.payment.method.popup.selectors.helpers';
var TRUE = 'True';
var SelectedPaymentInstrumentTypeValueSelector = createSelector([MakePaymentInstrumentSelector], function (currentPaymentInstrumentConfig) {
  return currentPaymentInstrumentConfig ? currentPaymentInstrumentConfig.Type : null;
});
var SelectedExternalGiftCardTypeSelector = createSelector([MakePaymentInstrumentSelector], function (currentPaymentInstrumentConfig) {
  return pathOr(null, ['ExternalGiftCard', 'Type'], currentPaymentInstrumentConfig);
});
var SelectedExternalBillTypeSelector = createSelector([MakePaymentInstrumentSelector], function (currentPaymentInstrumentConfig) {
  return pathOr(null, ['ExternalBill', 'Type'], currentPaymentInstrumentConfig);
});
export var PaymentInstrumentConfigurationSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.PaymentInstrumentConfiguration)], function (paymentInstrumentConfigurationAdditionalProperties) {
  return (paymentInstrumentConfigurationAdditionalProperties || []).map(function (paymentConfig) {
    if (!paymentConfig.Global) {
      var model = new PaymentInstrumentConfigurationAdditionalProperties();
      var additionalProperties = parseAdditionalProperties(model, paymentConfig.AdditionalProperties, paymentInstrumentConfigurationAdditionalPropertyDescriptors);
      return additionalProperties;
    }
  });
});
export var PaymentInstrumentTypeValueOptions = createSelector([MetadataSelectors.codes.PaymentInstrumentTypeSelector, ActionablesPerPaymentTypeSelector, SelectedPaymentInstrumentTypeValueSelector, AvailableTypesMapSelector, IsAllPaymentInstrumentTypesAvailableSelector], function (paymentInstrumentTypes, actionablesPerPaymentType, selectedPaymentInstrumentTypeValue, availablePaymentTypesMap, isAllPaymentInstrumentTypesAvailable) {
  var acceptedPaymentInstruments = paymentInstrumentTypes.filter(function (paymentInstrumentType) {
    var actionables = actionablesPerPaymentType[paymentInstrumentType.Value];
    var allowCreate = actionables ? actionables.userCreatable : false;
    var isNotOptedOut = EWalletHelper.isCreatablePaymentInstrumentType(paymentInstrumentType.Value);
    return allowCreate && isNotOptedOut && (isAllPaymentInstrumentTypesAvailable || !!pathOr(null, ["".concat(paymentInstrumentType.Value)], availablePaymentTypesMap));
  });
  return acceptedPaymentInstruments.map(function (paymentInstrumentType) {
    return {
      text: paymentInstrumentType.Name,
      selected: isPaymentInstrumentTypeValueOptionSelected(selectedPaymentInstrumentTypeValue, paymentInstrumentType, acceptedPaymentInstruments[0].Value),
      paymentInstrumentType: paymentInstrumentType
    };
  });
});
export var PaymentInstrumentTypeMaxFlagsSelector = createSelector([PaymentInstrumentsSelector, MetadataSelectors.codes.PaymentInstrumentTypeSelector, PaymentInstrumentConfigurationSelector], function (paymentInstruments, paymentInstrumentTypes, paymentInstrumentConfigurations) {
  var paymentInstrumentTypeCounts = countBy(prop('Type'))(paymentInstruments);
  var activePaymentInstrumentTypeCounts = countBy(prop('Type'))(filter(propEq(1, 'Status'))(paymentInstruments));
  return paymentInstrumentTypes.map(function (paymentInstrumentType) {
    var typeCount = paymentInstrumentTypeCounts[paymentInstrumentType.Value];
    var typeActiveCount = activePaymentInstrumentTypeCounts[paymentInstrumentType.Value];
    var typeConfiguration = paymentInstrumentConfigurations.find(function (config) {
      return config && config.paymentInstrumentTypeCode === paymentInstrumentType.Value;
    });
    var isMax = typeConfiguration && typeConfiguration.alertMax > 0 && typeCount >= typeConfiguration.alertMax;
    var isMaxActive = typeConfiguration && typeConfiguration.alertMaxActive > 0 && typeActiveCount >= typeConfiguration.alertMaxActive;
    return {
      value: paymentInstrumentType.Value,
      isMax: isMax,
      isMaxActive: isMaxActive
    };
  });
});
export var PaymentInstrumentTypeErrorsSelector = createSelector([PaymentInstrumentTypeMaxFlagsSelector, SelectedPaymentInstrumentTypeValueSelector], function (paymentInstrumentTypesMaxFlags, selectedPaymentInstrumentTypeValue) {
  var currentFlags = paymentInstrumentTypesMaxFlags.find(function (typeFlag) {
    return typeFlag.value === selectedPaymentInstrumentTypeValue;
  });
  if (currentFlags && (currentFlags.isMax || currentFlags.isMaxActive)) {
    return [currentFlags.isMax ? i18n.translate(CustomerCareLocaleKeys.E_WALLET.ERRORS.MAX) : '', currentFlags.isMaxActive ? i18n.translate(CustomerCareLocaleKeys.E_WALLET.ERRORS.MAX_ACTIVE) : ''].filter(function (r) {
      return r && r.length > 0;
    });
  } else {
    return [];
  }
});
export var SelectedPaymentTypeRequiresAddress = createSelector([SelectedPaymentInstrumentTypeValueSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.ExternalGiftCardType), SelectedExternalGiftCardTypeSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.ExternalBillType), SelectedExternalBillTypeSelector], function (selectedPaymentInstrumentType, externalGiftCardTypes, selectedExternalGiftCardTypeValue, externalBillTypes, selectedExternalBillTypeValue) {
  if (EWalletHelper.isAddressAlwaysRequiredForPaymentInstrumentType(selectedPaymentInstrumentType)) {
    return true;
  }
  if (EWalletHelper.isAddressNeverRequiredForPaymentInstrumentType(selectedPaymentInstrumentType)) {
    return false;
  }
  if (selectedPaymentInstrumentType === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.EXTERNAL_GIFT_CARD && externalGiftCardTypes && selectedExternalGiftCardTypeValue) {
    var selectedType = externalGiftCardTypes.find(function (type) {
      return type.Value.toString() === selectedExternalGiftCardTypeValue.toString();
    });
    return selectedType && cardTypeRequiresBillingAddress(selectedType);
  } else if (selectedPaymentInstrumentType === SUPPORTED_PAYMENT_INSTRUMENT_TYPES.EXTERNAL_BILL && externalBillTypes && selectedExternalBillTypeValue) {
    var selectedExternalBillType = externalBillTypes.find(function (type) {
      return type.Value.toString() === selectedExternalBillTypeValue.toString();
    });
    return selectedExternalBillType && cardTypeRequiresBillingAddress(selectedExternalBillType);
  }
  return false;
});
var ALLOW_MULTIPLE_PER_SUB_KEY = 'allow_multiple_per_subscriber';
var IS_ECARD_PERSISTABLE = 'is_persistable';
var ACCOUNT_NUMBER_REGEX_KEY = 'account_number_regex';
var ACCOUNT_NUMBER_REGEX_DESCRIPTION_KEY = 'account_number_regex_description';
export var SubscriberExternalGiftCardTypeOptionsWithRegexes = createSelector([PaymentInstrumentsSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.ExternalGiftCardType)], function (allSubscriberPaymentInstruments, allExternalGiftCardTypes) {
  var allowedEGCTypesForSubscriber = allExternalGiftCardTypes.filter(function (egcType) {
    var multipleAllowedPerSubscriberProperty = RamdaHelper.findByKey(ALLOW_MULTIPLE_PER_SUB_KEY, egcType.AdditionalProperties);
    var isEcardPersistableProperty = RamdaHelper.findByKey(IS_ECARD_PERSISTABLE, egcType.AdditionalProperties);
    var isECardPersistable = isEcardPersistableProperty && BoolHelper.getBoolOrDefault(isEcardPersistableProperty.Value, true);
    if (multipleAllowedPerSubscriberProperty && !BoolHelper.getBoolOrDefault(multipleAllowedPerSubscriberProperty.Value, true)) {
      return !subscriberHasExternalGiftCardOfType(allSubscriberPaymentInstruments, egcType) && isECardPersistable;
    }
    return isECardPersistable;
  });
  return allowedEGCTypesForSubscriber.map(function (egcType) {
    var accountNumberRegexProperty = RamdaHelper.findByKey(ACCOUNT_NUMBER_REGEX_KEY, egcType.AdditionalProperties);
    egcType.accountNumberRegex = accountNumberRegexProperty ? accountNumberRegexProperty.Value : '';
    var accountNumberRegexDescriptionProperty = RamdaHelper.findByKey(ACCOUNT_NUMBER_REGEX_DESCRIPTION_KEY, egcType.AdditionalProperties);
    egcType.accountNumberRegexDescription = accountNumberRegexProperty ? accountNumberRegexDescriptionProperty.Value : '';
    return egcType;
  });
});
export var IsAccountNumberEnabledSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.GiftCardConfiguration)], function (giftCardConfiguration) {
  var enableAccountNumberValues = [];
  giftCardConfiguration.forEach(function (giftCard) {
    var enableAccountNumber = giftCard.AdditionalProperties.find(function (additionalProperty) {
      return additionalProperty.Key === ENABLE_ACCOUNT_NUMBER_KEY;
    });
    enableAccountNumberValues.push(enableAccountNumber.Value);
  });
  return enableAccountNumberValues.includes(TRUE);
});
export var ExternalBillTypesSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.ExternalBillType)], function (externalBillTypes) {
  return (externalBillTypes || []).filter(function (billType) {
    // Find the is_two_step_payment additional property
    var isTwoStepPayment = RamdaHelper.findByKey(IS_TWO_STEP_PAYMENT_KEY, billType.AdditionalProperties);

    // Keep the bill type if the property doesn't exist or its value is not true
    return !isTwoStepPayment || !BoolHelper.getBoolOrDefault(isTwoStepPayment.Value, false);
  });
});